import {
  Action,
  AIONZ_DIALOG_DATA,
  AionzDialog,
  AionzDialogRef,
  CardTableModule,
  ConfirmationModalService,
  ModalTypes,
  PipesModule
} from '@aionzdev/design-system';
import { AbstractUserService } from '@aionzdev/modules';
import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  output,
  signal,
  ViewChild
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { UserResponse } from '@aionzdev/modules';
import { PurchaseSupplier, PurchaseSupplierLinkStatus } from '../../../interfaces/purchase-supplier';
import { PurchaseSuppliersListDialogResult } from '../../../interfaces/purchase-suppliers-list-dialog-result';
import { GenericPurchaseRequestDialogComponent } from '../generic-purchase-request-dialog/generic-purchase-request-dialog.component';
import { NewSupplierDialogComponent } from '../new-supplier-dialog/new-supplier-dialog.component';
import { KanbanChipBadgeComponent } from '../../atoms/kanban-chip-badge/kanban-chip-badge.component';

@Component({
  selector: 'aionz-purchase-suppliers-list-dialog',
  standalone: true,
  imports: [
    MatChipsModule,
    MatIconModule,
    CardTableModule,
    MatTableModule,
    MatButtonModule,
    MatSortModule,
    NgOptimizedImage,
    GenericPurchaseRequestDialogComponent,
    PipesModule,
    NgxMaskPipe,
    KanbanChipBadgeComponent
  ],
  providers: [provideNgxMask()],
  templateUrl: './purchase-suppliers-list-dialog.component.html',
  styleUrl: './purchase-suppliers-list-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PurchaseSuppliersListDialogComponent implements OnInit {
  private _dialogService = inject(ConfirmationModalService);
  private readonly userService = inject(AbstractUserService);
  createSupplier = output<PurchaseSupplier>();
  deleteSupplier = output<PurchaseSupplier>();
  approveSuggestedSupplier = output<PurchaseSupplier>();
  searchCNPJ = output<{ cnpj: string; callback: (user: UserResponse | null) => void }>();

  protected readonly PurchaseSupplierLinkStatus = PurchaseSupplierLinkStatus;

  public suppliers: PurchaseSupplier[] = inject(AIONZ_DIALOG_DATA);
  public dialog = inject(AionzDialog);
  public dialogRef = inject(
    AionzDialogRef<PurchaseSuppliersListDialogComponent, PurchaseSuppliersListDialogResult>
  );

  @ViewChild(MatSort) sort!: MatSort;

  protected displayedColumns: string[] = [
    // 'name', — desativada a pedido do usuário (redundante com fantasyName)
    'fantasyName',
    'cnpj',
    'phone',
    'email',
    'source',
    'status',
    'actions'
  ];
  protected dataSource = signal(new MatTableDataSource<PurchaseSupplier>([]));

  ngOnInit(): void {
    this.dataSource.set(new MatTableDataSource(this.suppliers));
  }

  ngAfterViewInit() {
    this.dataSource().sort = this.sort;
  }

  protected close(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  protected addNew(): void {
    const dialogRef = this.dialog.open<NewSupplierDialogComponent, void, PurchaseSupplier>(
      NewSupplierDialogComponent,
      // Foco inicial no CNPJ (cdkFocusInitial): é por ele que o preenchimento começa.
      { autoFocus: true }
    );

    dialogRef.componentInstance.searchCNPJ.subscribe(event => {
      this.searchCNPJ.emit(event);
      this.userService.findByCNPJ(event.cnpj).subscribe({
        next: user => event.callback(user ?? null),
        error: () => event.callback(null)
      });
    });

    dialogRef.afterClosed().subscribe(supplier => {
      if (supplier) {
        this.createSupplier.emit(supplier);
      }
    });
  }

  /**
   * Badge da coluna "Status" — sempre retorna algo (nunca null): fornecedores
   * sem linkStatus (manual/integração/histórico) já deveriam vir APROVADO do
   * backend por padrão (BuscarPedidosAdmin.ts, coluna com DEFAULT 'APROVADO'
   * na migration), mas o fallback aqui cobre qualquer caso de borda sem
   * deixar a coluna vazia. `label` vazio (só ícone) — o texto completo fica
   * disponível via `tooltip` (AionzTooltip, ver kanban-chip-badge).
   */
  protected statusBadge(
    supplier: PurchaseSupplier
  ): { label: string; tooltip: string; icon: string; color: string } {
    switch (supplier.linkStatus) {
      case PurchaseSupplierLinkStatus.PENDENTE:
        return {
          label: '',
          tooltip: 'Sugestão pendente',
          icon: 'schedule',
          color: 'var(--color-warning-default)'
        };
      case PurchaseSupplierLinkStatus.REJEITADO:
        return {
          label: '',
          tooltip: 'Rejeitado',
          icon: 'cancel',
          color: 'var(--color-error-default)'
        };
      case PurchaseSupplierLinkStatus.APROVADO:
      default:
        return {
          label: '',
          tooltip: 'Presente na cotação',
          icon: 'check_circle',
          color: 'var(--color-success-default)'
        };
    }
  }

  protected readonly isSuggestedByCotAI = (supplier: PurchaseSupplier): boolean =>
    supplier.source === 'eBarn CotAI';

  protected onApproveSuggestedSupplier(supplier: PurchaseSupplier): void {
    this.approveSuggestedSupplier.emit(supplier);
  }

  protected onDeleteSupplier(supplier: PurchaseSupplier): void {
    this._dialogService
      .open(
        {
          headerTitle: 'Remover Fornecedor',
          title: 'Deseja remover esse fornecedor dessa cotação?',
          description: 'O fornecedor será removido e não poderá enviar o orçamento para cotação.',
          labelConfirm: 'Desejo remover',
          labelCancel: 'Cancelar'
        },
        ModalTypes.DANGER
      )
      .subscribe(confirmed => {
        if (confirmed === Action.CONFIRM) {
          this.deleteSupplier.emit(supplier);
        }
      });
  }

  public updatedSuppliersList(suppliers: PurchaseSupplier[]) {
    this.suppliers = suppliers;
    this.dataSource.set(new MatTableDataSource(this.suppliers));
  }
}
